import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, NgModule, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

import { reset } from '../+state/configurator/configurator.actions';
import { ConfiguratorFacade } from '../+state/configurator/configurator.facade';
import { ScrollService } from '../core/scroll.service';
import { TrackingService } from '../core/tracking.service';
import { FooterMinimalComponent } from '../footer-minimal/footer-minimal.component';
import { PortalComponent } from '../portal/portal.component';
import { UtilsModule } from '../utils/utils.module';
import { Configurator360Component } from './components';
import { stepTrackingKeyLookup } from './configurator-settings';
import { ConfiguratorFormComponent } from './form';
import {
    ConfiguratorFooterComponent,
    ConfiguratorHeaderComponent,
    ConfiguratorTabsContainerComponent,
    ConfiguratorTabsNavComponent,
} from './navigation';
import { ConfiguratorReceiptComponent } from './receipt/configurator-receipt.component';
import { ConfiguratorSummaryStepComponent } from './summary';
import { DataByIdPipe } from './utils/data-by-id.pipe';

@Component({
    selector: 'ncg-configurator',
    template: `
        <div class="configurator" *ngIf="configuratorFacade as state">
            <ncg-portal outletId="#header-portal">
                <ncg-configurator-header></ncg-configurator-header>
            </ncg-portal>
            <ng-container *ngIf="state.step$ | async as step">
                <ng-container [ngSwitch]="step">
                    <ng-container *ngSwitchCase="['configuration', 'summary'].includes(step) ? step : ''">
                        <ncg-configurator-360></ncg-configurator-360>
                    </ng-container>
                    <ng-container *ngSwitchCase="'configuration'">
                        <ncg-configurator-tabs-nav></ncg-configurator-tabs-nav>
                    </ng-container>
                </ng-container>
            </ng-container>
            <div class="configurator__content" [ngSwitch]="state.step$ | async">
                <ng-container *ngSwitchCase="'configuration'">
                    <ncg-configurator-tabs-container></ncg-configurator-tabs-container>
                    <ncg-portal outletId="#footer-portal">
                        <ncg-configurator-footer></ncg-configurator-footer>
                    </ncg-portal>
                </ng-container>
                <ng-container *ngSwitchCase="'summary'">
                    <ncg-configurator-summary-step></ncg-configurator-summary-step>
                </ng-container>
                <ng-container *ngSwitchCase="'mail'">
                    <ncg-configurator-form type="to_me"></ncg-configurator-form>
                </ng-container>
                <ng-container *ngSwitchCase="'dealer'">
                    <ncg-configurator-form type="to_dealer"></ncg-configurator-form>
                </ng-container>
                <ng-container *ngSwitchCase="'mail_receipt'">
                    <ncg-configurator-receipt type="to_me"></ncg-configurator-receipt>
                </ng-container>
                <ng-container *ngSwitchCase="'dealer_receipt'">
                    <ncg-configurator-receipt type="to_dealer"></ncg-configurator-receipt>
                </ng-container>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfiguratorComponent implements OnInit, OnDestroy {
    private originalScrollDefault: boolean;
    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly scrollService: ScrollService,
        private readonly trackingService: TrackingService,
        public readonly configuratorFacade: ConfiguratorFacade
    ) {
        combineLatest([this.configuratorFacade.trackingStep$, this.configuratorFacade.model$])
            .pipe(
                distinctUntilChanged(([prev], [curr]) => prev.step === curr.step),
                takeUntil(this.unsubscribe)
            )
            // eslint-disable-next-line id-blacklist
            .subscribe(([{ number, step }, model]) => {
                // Return if any params does match desired tracking
                // eslint-disable-next-line id-blacklist
                if (step === 'dealer_receipt' || step === 'mail_receipt' || !model || number < 1) {
                    return;
                }
                // eslint-disable-next-line id-blacklist
                this.trackingService.TrackConfiguratorStep(`${number}. ${stepTrackingKeyLookup[step]}`, model.title || '');
            });
    }

    ngOnInit(): void {
        // Save value so we can restore it once configurator is destroyed
        this.originalScrollDefault = this.scrollService.enableDefaultScrollValue;
        this.scrollService.toggleEnableDefaultScroll(false);
    }

    ngOnDestroy(): void {
        this.scrollService?.toggleEnableDefaultScroll(this.originalScrollDefault);
        this.configuratorFacade?.dispatch(reset());
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
@NgModule({
    declarations: [ConfiguratorComponent],
    exports: [ConfiguratorComponent],
    imports: [
        CommonModule,
        UtilsModule,
        RouterModule,
        TranslateModule.forChild(),
        PortalComponent,
        ConfiguratorTabsNavComponent,
        ConfiguratorHeaderComponent,
        ConfiguratorFooterComponent,
        ConfiguratorTabsContainerComponent,
        ConfiguratorSummaryStepComponent,
        FooterMinimalComponent,
        ConfiguratorFormComponent,
        ConfiguratorReceiptComponent,
        DataByIdPipe,
        Configurator360Component,
    ],
})
export class ConfiguratorComponentModule {}
